"use client";

import type { Locale } from "@commerce-frontend/i18n/types";
import type { ProductDetailPageFragmentFragment as ProductDetailPageFragment } from "@commerce-frontend/types";
import { Button } from "@commerce-frontend/ui/base/Button";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import { Option, Select } from "@commerce-frontend/ui/base/Select/Select";
import { typography } from "@commerce-frontend/ui/base/Typography/typography";
import { Thumbnail } from "@commerce-frontend/ui/components/Thumbnail/Thumbnail";
import { cn } from "@commerce-frontend/ui/helpers/styles";
import { Link } from "@commerce-frontend/ui/primitives/Link";
import { Sticky } from "@commerce-frontend/ui/primitives/Sticky/Sticky";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { AddToCartButton } from "~/components/AddToCartButton/AddToCartButton";
import { PriceTag } from "~/components/PriceTag/PriceTag";
import { Ribbon } from "~/components/Ribbon/Ribbon";
import { TrustedShopsRating } from "~/components/TrustedShopsRating/TrustedShopsRating";
import { VariantButton } from "~/components/VariantButton/VariantButton";
import { productDetailHref } from "~/lib/link-builder";
import { useStoreConfig } from "~/lib/store-config/context";
import { ProductSizeGuideDialog } from "./_components/ProductSizeGuideDialog/ProductSizeGuideDialog";
import { QuantityLabels } from "./_components/QuantityLabels/QuantityLabels";

type Props = {
	initialSku?: string;
	product: ProductDetailPageFragment;
	locale: Locale;
};

export const ProductBuySection = ({ product, initialSku, locale }: Props) => {
	// @TODO: Either we can have the selected variant as part of the state here,
	// or get the sku from the parent (query param) and make this a server component
	const [selectedSku, setSelectedSku] = useState(initialSku);
	// @TODO: Part of quantity selector - ZEEMAN-411
	const [quantity, setQuantity] = useState(1);
	const [isSizeFocused, setIsSizeFocused] = useState(false);
	const t = useTranslations("ProductBuySection");
	const storeConfig = useStoreConfig();

	const selectedVariant = product.variants.find(
		(v) =>
			v.sku === selectedSku &&
			v.availability !== "OUT_OF_STOCK" &&
			product.startingPrice?.gross.centAmount &&
			v.price?.gross?.centAmount,
	);

	const handlePickSizeClick = () => {
		if (
			!selectedVariant?.sku &&
			product.availability === "IN_STOCK" &&
			product.startingPrice?.gross?.centAmount
		) {
			setIsSizeFocused(true);
			setTimeout(() => setIsSizeFocused(false), 1000);
		}
	};

	const orderQuantities = useMemo(() => {
		const result: number[] = [];
		if (selectedVariant) {
			const min = selectedVariant.orderConfiguration?.min ?? 1;
			const max = selectedVariant.orderConfiguration?.max ?? 100;
			const stepSize = Math.max(selectedVariant.orderConfiguration?.stepSize ?? 1, 1);

			for (let step = min; step <= max; step += stepSize) {
				result.push(step);
			}
		}
		return result;
	}, [selectedVariant]);

	useEffect(() => {
		// If the selected variant changes, or the available options change, make sure the selected option is still valid
		if (orderQuantities.length && !orderQuantities.includes(quantity)) {
			setQuantity(orderQuantities[0]);
		}
	}, [orderQuantities, quantity]);

	const renderPriceTag = () => {
		if (selectedVariant) {
			const { regularPrice, price } = selectedVariant;
			if (!price) {
				return null;
			}

			const isDiscount =
				regularPrice?.gross.centAmount && price.gross.centAmount !== regularPrice.gross.centAmount;

			return (
				<div className="inline-flex gap-md">
					<PriceTag
						size="md"
						price={price.gross}
						discountPrice={isDiscount ? regularPrice?.gross : undefined}
						locale={locale}
					/>
				</div>
			);
		}

		const { startingPrice } = product;

		if (!startingPrice?.gross?.centAmount) {
			return null;
		}

		return <PriceTag size="md" price={startingPrice.gross} locale={locale} />;
	};

	const neckline = product.neckline ?? "";
	const rawmaterial = product.rawMaterial ?? "";
	const brand = product.brand ?? "";
	const denier = product.denier ?? "";
	const fit = product.fit ?? "";
	const packsize = product.packSize ? t("pack-size", { size: product.packSize }) : "";

	const underline = [brand, rawmaterial, denier, neckline, fit, packsize]
		.filter(Boolean)
		.join(", ");

	const marketingRibbon = product.marketingRibbons?.[0];
	const promotionRibbons = product.promotionRibbons;

	return (
		<section
			className="relative flex flex-col gap-6xl pt-7xl lg:px-4xl lg:pt-9xl"
			aria-label={t("aria-name")}
		>
			<div className="relative">
				{marketingRibbon || promotionRibbons?.length ? (
					<div className="flex flex-col items-start gap-md mb-3xl">
						{marketingRibbon ? (
							<Ribbon
								variant="marketing"
								className="inline-flex"
								label={marketingRibbon?.label ?? ""}
								color={marketingRibbon.color}
							/>
						) : null}
						{promotionRibbons?.map((promotionRibbon) => (
							<Ribbon
								variant="promotion"
								className="inline-flex"
								key={`promotion-${promotionRibbon?.label}`}
								label={promotionRibbon?.label ?? ""}
								color={promotionRibbon?.color}
							/>
						))}
					</div>
				) : null}

				<h1 className="display-md lg:display-lg gap-2 font-semibold">{product.name}</h1>

				{underline ? <p className={typography("lead")}>{underline}</p> : null}

				{renderPriceTag()}

				<QuantityLabels product={product} selectedVariant={selectedVariant} />
			</div>

			<div className="flex flex-col gap-xl items-start">
				{product.reviews?.averageRating && storeConfig.etrustedEnabled ? (
					<Link href="#product-reviews">
						<TrustedShopsRating rating={product.reviews?.averageRating} />
					</Link>
				) : null}

				<Link
					className="group inline-flex items-center gap-md self-start rounded-none py-xxs font-semibold text-brand transition-all hover:text-brand-600 focus:bg-orange-dark-200"
					href="#product-information"
				>
					<span className="border-b border-brand leading-tight transition-all group-hover:border-transparent">
						{t("product-information")}
					</span>
					<Icon
						icon="arrow-square-down"
						className="size-5 bg-brand transition-all group-hover:bg-brand-600"
					/>
				</Link>
			</div>

			<div className="relative">
				{/* Color variants (Related products) */}
				{product.relatedProducts && product.relatedProducts?.length > 1 && (
					<div className="mb-4xl">
						<div className="mb-xl block font-semibold">{t("choose-variant")}</div>
						<div className="flex flex-wrap gap-xs overflow-y-scroll lg:overflow-visible">
							{product.relatedProducts.map((relatedProduct, i) => {
								const variantsInStock = relatedProduct?.variants?.some(
									(v) => v.availability !== "OUT_OF_STOCK" && v.price?.gross?.centAmount,
								);

								return (
									relatedProduct.image && (
										<Thumbnail
											image={{
												filename: relatedProduct.image.url,
												alt: relatedProduct.image.alt,
											}}
											available={
												relatedProduct.availability === "IN_STOCK" &&
												Boolean(relatedProduct.startingPrice?.gross?.centAmount) &&
												variantsInStock
											}
											selected={product.primaryVariant.sku === relatedProduct.primaryVariant.sku}
											index={i}
											href={productDetailHref(relatedProduct.slug)}
											key={relatedProduct.id}
											scroll={false}
										/>
									)
								);
							})}
						</div>
					</div>
				)}

				{/* Size / Additional variants */}
				{product.variants.length > 0 && (
					<div
						className={`mb-4xl py-xs transition-all ${isSizeFocused ? "bg-gray-100 pl-xs" : ""}`}
					>
						<div className="mb-xl flex items-center gap-2xl">
							<div className="font-semibold">{t("choose-size")}</div>

							<ProductSizeGuideDialog sizeGuide={product.sizeGuide} locale={locale} />
						</div>

						<div className="inline-flex flex-wrap gap-xs">
							{product.variants.map((v, i) => (
								<VariantButton
									isDisabled={
										v.availability === "OUT_OF_STOCK" ||
										!v.price?.gross?.centAmount ||
										!product.startingPrice?.gross.centAmount
									}
									isSelected={selectedVariant?.sku === v.sku}
									onClick={() => {
										if (!v.sku) return;
										const sku = v?.sku !== selectedVariant?.sku ? v?.sku : undefined;
										setSelectedSku(sku);
										const url = new URL(window.location.href);
										if (sku) {
											url.searchParams.set("sku", sku);
										} else {
											url.searchParams.delete("sku");
										}
										window.history.replaceState(null, "", url.toString());
									}}
									key={v.id}
								>
									{v.attributes?.find((a) => a.name === "size")?.value ?? `Variant ${i + 1}`}
								</VariantButton>
							))}
						</div>
					</div>
				)}

				{/* out of stock when only one variant, we can redirect to another product */}
				{/* {variant.availability === "OUT_OF_STOCK" && product.variants.length === 1 ? (
				<>
					<div className="relative inline self-start px-6 py-3">
						{t("one-variant-not-available")}
					</div>
					<p>{t("choose-different-product")}</p>
				</>
			) : undefined} */}
				{/* out of stock, multiple variants */}
				{/* {variant.availability === "OUT_OF_STOCK" && product.variants.length > 1 ? (
				<div>{t("multiple-variants-not-available")}</div>
			) : undefined} */}

				{/* Add to cart */}

				{
					// @TODO: add to cart fake focus on disabled button
					// eslint-disable-next-line jsx-a11y/no-static-element-interactions
					<div className="flex gap-2" onClick={handlePickSizeClick} onKeyDown={handlePickSizeClick}>
						{/* @TODO: Quantity selector - ZEEMAN-411 */}
						{selectedVariant?.sku ? (
							<Select
								className="w-28 text-center font-sans [text-align-last:center]"
								variant="secondary"
								value={String(quantity)}
								disabled={orderQuantities.length === 0}
								onChange={(value) => {
									setQuantity(Number(value));
								}}
							>
								{orderQuantities
									.filter((quantity) => quantity <= (selectedVariant?.availabilityQuantity ?? 0))
									.map((quantity) => (
										<Option key={quantity} value={String(quantity)}>
											{quantity}
										</Option>
									))}
							</Select>
						) : null}

						{/* @TODO: Add to cart - ZEEMAN-239 */}

						<Sticky
							stuckClassName="inset-y-0 -mx-4 w-[100vw] lg:w-full lg:p-0 lg:m-0 px-4 sm:-mx-5 sm:px-5 ring-offset-8 shadow-top lg:shadow-none"
							top={0}
							bottom={0}
							className={cn(
								"bottom-0 left-0 top-0 z-10 -my-4 w-full py-4 lg:relative",
								"flex flex-wrap items-center justify-between gap-2",
							)}
						>
							{selectedVariant?.sku ? (
								selectedVariant.price?.gross?.centAmount &&
								(selectedVariant.availability === "IN_STOCK" ? (
									<AddToCartButton
										product={product}
										sku={selectedVariant.sku}
										quantity={quantity}
										categoryId={product.primaryCategory?.id}
										className="w-full"
										id="add-to-cart-button"
									/>
								) : (
									<Button variant="primary" disabled>
										{t("one-variant-not-available")}
									</Button>
								))
							) : (
								<Button className="w-full" size="xl" variant="secondary" disabled>
									{t("pick-size")}
								</Button>
							)}
						</Sticky>
					</div>
				}
			</div>
		</section>
	);
};
